import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchNewsById, fetchNews, fetchTrending } from "@/lib/api";
import { ExternalLink, ArrowLeft, Loader2, Clock, User } from "lucide-react";
import { useLang, formatRelativeTime, categoryName } from "@/i18n";
import { NewsCard } from "@/components/NewsCard";
import MostRead from "@/components/MostRead";

export default function NewsDetail() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    window.scrollTo(0, 0);
    (async () => {
      try {
        const detail = await fetchNewsById(id);
        if (cancelled) return;
        setItem(detail);
        const [rel, trend] = await Promise.all([
          fetchNews({ lang: detail.language, category: detail.category, limit: 5 }),
          fetchTrending({ lang: detail.language, limit: 5 }),
        ]);
        if (cancelled) return;
        setRelated(rel.filter((r) => r.id !== detail.id).slice(0, 4));
        setTrending(trend);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, lang]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 flex items-center justify-center">
        <Loader2 className="animate-spin text-red-600" size={32} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="headline text-3xl">404</h1>
        <p className="mt-2 text-neutral-600">News not found.</p>
        <Link to="/" className="mt-6 inline-block text-red-600 underline">← Home</Link>
      </div>
    );
  }

  return (
    <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="news-detail-page">
      <Link to={`/category/${item.category}`} className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-neutral-400 hover:text-red-500 mb-6" data-testid="back-to-category">
        <ArrowLeft size={14} /> {categoryName(item.category, item.language)}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <span className="eyebrow text-red-500" data-testid="detail-category">{categoryName(item.category, item.language)}</span>
          <h1 className="headline text-4xl sm:text-5xl lg:text-6xl mt-3 text-balance text-white" data-testid="detail-title">
            {item.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-neutral-400 font-mono uppercase tracking-wider border-y border-neutral-800 py-3">
            <span className="flex items-center gap-1.5"><User size={14} />{item.source}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} />{formatRelativeTime(item.published_at, item.language)}</span>
            <span className="ml-auto text-neutral-500">{item.views.toLocaleString()} views</span>
          </div>

          <div className="mt-8 aspect-[16/9] overflow-hidden bg-neutral-900">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1495020689067-958852a7765e?w=1200"; }}
            />
          </div>

          <div className="mt-8 max-w-none font-body text-neutral-300 leading-relaxed">
            <p className="text-xl font-medium text-neutral-200 leading-snug mb-6" data-testid="detail-description">
              {item.description}
            </p>
            <p className="text-base">
              {item.language === "tr"
                ? `Bu haberin tam içeriği ${item.source} tarafından sağlanmaktadır. Aşağıdaki bağlantıdan tüm detaylara ulaşabilirsiniz.`
                : `Full coverage of this story is published by ${item.source}. Tap the link below to read the complete article on the original site.`}
            </p>
          </div>

          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-white hover:bg-red-600 text-black hover:text-white text-sm font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors"
            data-testid="detail-source-link"
          >
            {item.language === "tr" ? "Kaynaktan Devamını Oku" : "Read full story on source"} <ExternalLink size={14} />
          </a>

          {related.length > 0 && (
            <section className="mt-16">
              <div className="border-b-2 border-white pb-2 mb-6">
                <h3 className="headline text-2xl text-white">{item.language === "tr" ? "İlgili Haberler" : "Related"}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {related.map((r) => <NewsCard key={r.id} item={r} />)}
              </div>
            </section>
          )}
        </div>

        <aside className="lg:col-span-4">
          <MostRead items={trending} />
        </aside>
      </div>
    </article>
  );
}
